import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../../components/ui/Sidebar";
import Sidebar from "../../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";
import { useCreateExpense } from "./useCreateExpense";
import { buildExpensePayload } from "./logic/buildExpensePayload";
import { useGetVendors } from "pages/vendor-management/useGetVendors";

const CATEGORIES = [
  "Administrative",
  "Advertisement",
  "Fees",
  "Office Expense",
  "Rent",
  "Salaries",
  "Utilities",
  "Other",
];

const VAT_RATES = [
  { label: "0% (Exempt)", value: 0 },
  { label: "15% (Standard)", value: 15 },
  { label: "Custom", value: "custom" },
];

const EMPTY = {
  vendorId: "",
  date: new Date().toISOString().split("T")[0],
  expenseNumber: "EXP-001",
  vatRate: 15,
  customVat: "",
  showCustomVat: false,
  category: "",
  vendorName: "",
  amount: "",
  comments: "",
};

const SectionCard = ({
  icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  children,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
  >
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div
          className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}
        >
          <Icon name={icon} size={20} className={iconColor} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground dark:text-white">
            {title}
          </h3>
          <p className="text-sm text-muted-foreground dark:text-gray-300">
            {subtitle}
          </p>
        </div>
      </div>
      {children}
    </div>
  </motion.div>
);

const TextInput = ({ error, className = "", ...props }) => (
  <input
    {...props}
    className={`w-full px-3 py-2.5 border rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm transition-colors ${
      error ? "border-red-400" : "border-gray-200 dark:border-gray-600"
    } ${className}`}
  />
);

const SelectInput = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
  >
    {children}
  </select>
);

const Field = ({ label, required, error, hint, children }) => (
  <div>
    {label && (
      <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {hint && !error && (
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    )}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const OperatingExpenseContent = () => {
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("pages");
  const existing = location.state?.expense || null;

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const createExpense = useCreateExpense();
  const { data: vendors = [] } = useGetVendors();

  useEffect(() => {
    if (existing) setForm({ ...EMPTY, ...existing });
  }, [existing]);

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const handleVatChange = (value) => {
    set("vatRate", value === "custom" ? "custom" : parseFloat(value));
    set("showCustomVat", value === "custom");
    if (value !== "custom") set("customVat", "");
  };

  const validate = () => {
    const e = {};
    if (!form.date) e.date = "Date is required.";
    if (!form.expenseNumber.trim())
      e.expenseNumber = "Expense number is required.";
    if (!form.category) e.category = "Category is required.";
    if (!form.amount || parseFloat(form.amount) <= 0)
      e.amount = "Amount must be greater than 0.";
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      toast.error(t("purchaseExpenseForms.saveError"));
      return;
    }

    setSaving(true);
    try {
      await createExpense.mutateAsync(
        buildExpensePayload(form, "operating_expense"),
      );
      toast.success(t("purchaseExpenseForms.operatingRecorded"));
      navigate("/expenses");
    } catch (err) {
      toast.error(err?.message || t("purchaseExpenseForms.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const effectiveVat = form.showCustomVat
    ? parseFloat(form.customVat) || 0
    : parseFloat(form.vatRate) || 0;
  const amount = parseFloat(form.amount) || 0;
  const vatAmount = amount * (effectiveVat / 100);
  const total = amount + vatAmount;

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Sticky Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-40 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-8"
        >
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/expenses")}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/20 rounded flex items-center justify-center">
                    <Icon
                      name="Briefcase"
                      size={13}
                      className="text-blue-600 dark:text-blue-400"
                    />
                  </div>
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                    {t("purchaseExpenseForms.operatingExpense")}
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-foreground dark:text-white">
                  {existing
                    ? t("purchaseExpenseForms.editOperating")
                    : t("purchaseExpenseForms.newOperating")}
                </h1>
                <p className="text-sm text-muted-foreground dark:text-gray-300">
                  {t("purchaseExpenseForms.operatingDescription")}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => navigate("/expenses")}
              >
                {t("purchaseExpenseForms.cancel")}
              </Button>
              <Button
                variant="primary"
                size="md"
                iconName={saving ? "Loader2" : "Save"}
                onClick={handleSave}
                disabled={saving}
                className="shadow-sm"
              >
                {saving
                  ? t("purchaseExpenseForms.saving")
                  : existing
                    ? t("purchaseExpenseForms.updateExpense")
                    : t("purchaseExpenseForms.saveExpense")}
              </Button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3 space-y-6">
            {/* Details */}
            <SectionCard
              icon="Briefcase"
              iconBg="bg-blue-100 dark:bg-blue-900/20"
              iconColor="text-blue-600 dark:text-blue-400"
              title="Expense Details"
              subtitle="Fill in the details for this operating expense"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Date" required error={errors.date}>
                  <TextInput
                    type="date"
                    value={form.date}
                    error={errors.date}
                    onChange={(e) => set("date", e.target.value)}
                  />
                </Field>

                <Field
                  label="Expense Number"
                  required
                  error={errors.expenseNumber}
                >
                  <TextInput
                    value={form.expenseNumber}
                    error={errors.expenseNumber}
                    placeholder="EXP-001"
                    className="font-mono"
                    onChange={(e) => set("expenseNumber", e.target.value)}
                  />
                </Field>

                <Field label="Category" required error={errors.category}>
                  <SelectInput
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                  >
                    <option value="">Select category</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </SelectInput>
                  {errors.category && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.category}
                    </p>
                  )}
                </Field>

                <Field
                  label="Vendor"
                  hint="Link this expense to a vendor (optional)"
                >
                  <SelectInput
                    value={form.vendorId}
                    onChange={(e) => {
                      const v = vendors.find(
                        (x) => String(x.id || x._id) === e.target.value,
                      );
                      set("vendorId", e.target.value);
                      set("vendorName", v?.name || "");
                    }}
                  >
                    <option value="">Select vendor (optional)</option>
                    {vendors.map((v) => (
                      <option key={v.id || v._id} value={v.id || v._id}>
                        {v.name}
                      </option>
                    ))}
                  </SelectInput>
                </Field>

                {/* VAT Rate */}
                <Field label="VAT Rate">
                  <SelectInput
                    value={form.vatRate}
                    onChange={(e) => handleVatChange(e.target.value)}
                  >
                    {VAT_RATES.map((v) => (
                      <option key={v.label} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </SelectInput>
                  {form.showCustomVat && (
                    <TextInput
                      type="number"
                      value={form.customVat}
                      min="0"
                      max="100"
                      placeholder="Enter VAT %"
                      onChange={(e) => set("customVat", e.target.value)}
                      className="mt-2"
                    />
                  )}
                </Field>

                {/* Amount */}
                <Field
                  label="Amount (excl. VAT)"
                  required
                  error={errors.amount}
                >
                  <div className="relative">
                    <span className="absolute start-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      SR
                    </span>
                    <TextInput
                      type="number"
                      value={form.amount}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      error={errors.amount}
                      className="ps-9"
                      onChange={(e) => set("amount", e.target.value)}
                    />
                  </div>
                </Field>

                {/* Comments */}
                <div className="md:col-span-2">
                  <Field label="Comments">
                    <textarea
                      value={form.comments}
                      onChange={(e) => set("comments", e.target.value)}
                      placeholder="Additional notes about this expense..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                    />
                  </Field>
                </div>
              </div>
            </SectionCard>

            {/* Bottom actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 flex justify-end gap-3"
            >
              <Button
                variant="outline"
                size="md"
                onClick={() => navigate("/expenses")}
              >
                {t("purchaseExpenseForms.cancel")}
              </Button>
              <Button
                variant="primary"
                size="md"
                iconName={saving ? "Loader2" : "Save"}
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? t("purchaseExpenseForms.saving")
                  : existing
                    ? t("purchaseExpenseForms.updateExpense")
                    : t("purchaseExpenseForms.saveExpense")}
              </Button>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-4">
              {/* Summary */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Icon name="BarChart2" size={18} className="text-primary" />
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    Summary
                  </h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-medium text-foreground">
                      SR{" "}
                      {amount.toLocaleString("en-SA", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      VAT ({effectiveVat}%)
                    </span>
                    <span className="font-medium text-foreground">
                      SR{" "}
                      {vatAmount.toLocaleString("en-SA", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-foreground">
                        Total
                      </span>
                      <span className="text-lg font-bold text-primary">
                        SR{" "}
                        {total.toLocaleString("en-SA", {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Checklist */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Icon
                    name="CheckCircle2"
                    size={18}
                    className="text-primary"
                  />
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    Checklist
                  </h3>
                </div>
                <div className="space-y-2.5">
                  {[
                    { label: "Date set", ok: !!form.date },
                    { label: "Expense #", ok: !!form.expenseNumber.trim() },
                    { label: "Category", ok: !!form.category },
                    {
                      label: "Amount",
                      ok: !!form.amount && parseFloat(form.amount) > 0,
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Icon
                          name={item.ok ? "CheckCircle2" : "Circle"}
                          size={15}
                          className={
                            item.ok
                              ? "text-green-500"
                              : "text-gray-300 dark:text-gray-600"
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          {item.label}
                        </span>
                      </div>
                      <span
                        className={`text-xs font-medium ${item.ok ? "text-green-600 dark:text-green-400" : "text-gray-400"}`}
                      >
                        {item.ok ? "Done" : "Pending"}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OperatingExpense = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <OperatingExpenseContent />
    </div>
  </SidebarProvider>
);

export default OperatingExpense;
